import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Input } from 'antd';
import Box from '@mui/material/Box';
import axiosInstance from '../apis/axiosInstance';
import OrderTable from '../components/OrderTable';
import { debounce } from 'lodash';

const POLL_INTERVAL = 20000;

const OrderPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const latestOrderRef = useRef({ latestId: null });
  const pollRef = useRef(null);

  const fetchOrders = useCallback(async (keyword = '') => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/get_order_table_data/', {
        currentpage: 1,
        pagesize: 10,
        search: keyword
      });
      const data = response.data || [];
      setOrders(data);
      if (data.length > 0) {
        latestOrderRef.current.latestId = String(data[0].orderId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const pollOrderCount = useCallback(async () => {
    if (searchKeyword) return;
    try {
      const response = await axiosInstance.post('/api/get_order_count/');
      const { latest_order_id } = response.data;
      if (String(latestOrderRef.current.latestId) !== String(latest_order_id)) {
        fetchOrders('');
      }
    } catch (error) {
      console.error(error);
    }
  }, [fetchOrders, searchKeyword]);

  useEffect(() => {
    fetchOrders('');
  }, [fetchOrders]);

  useEffect(() => {
    pollOrderCount();
    pollRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        pollOrderCount();
      }
    }, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [pollOrderCount]);

  const debouncedSearch = useCallback(
    debounce((keyword) => {
      fetchOrders(keyword);
    }, 300),
    [fetchOrders]
  );

  const handleSearch = (value) => {
    setSearchKeyword(value);
    debouncedSearch(value);
  };

  return (
    <Box>
      <Box sx={{ height: '60px', width: '100%', display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
        <Box
          sx={{
            height: '100%',
            width: '100%',
            maxWidth: '1350px',
            bgcolor: '#fff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
          }}
        >
          <Box sx={{ marginLeft: { xs: '10px', sm: '20px' } }}>
            <Button type="primary">Create New</Button>
          </Box>
          <Box sx={{ marginRight: { xs: '10px', sm: '20px' }, maxWidth: '300px', flex: '1' }}>
            <Input
              placeholder="Search by keyword ..."
              style={{ width: '100%' }}
              value={searchKeyword}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '1350px',
            bgcolor: '#fff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '5px',
            margin: '0 10px',
            '& .ant-table-body': {
              scrollbarWidth: 'thin',
              scrollbarColor: '#888 #f1f1f1',
              '&::-webkit-scrollbar': { width: '6px', height: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#888', borderRadius: '3px' },
              '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' }
            },
            '& .ant-pagination': { paddingRight: '20px' }
          }}
        >
          <OrderTable orders={orders} loading={loading} searchKeyword={searchKeyword} />
        </Box>
      </Box>
    </Box>
  );
};

export default OrderPage;